import {
  MapOverlayMachineEventsEnum,
  useMapOverlayMachine,
} from 'components/Overlays/MapOverlayMachine';
import AppStoreContext from 'contexts/AppStoreContext';
import Immutable from 'immutable';
import React, { useContext, useEffect } from 'react';
import { UpdatePunch } from 'records/interactions/UpdatePunch';
import { Markup, MarkupId, MarkupMap } from 'records/Markup';
import { Project } from 'records/Project';
import { Punch } from 'records/Punch';
import { PunchQuery } from 'records/PunchQuery';
import { Sheet } from 'records/Sheet';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import PunchListHeader from './PunchListHeader';
import PunchListRow from './PunchListRow';

type PunchListProps = {
  sheets: Immutable.List<Sheet>;
  resetPunchQuery: boolean;
  isOnMap: boolean;
} & WithAppStoreProps;

const PunchList: React.FC<PunchListProps> = ({
  sheets,
  resetPunchQuery,
  isOnMap,
}) => {
  const { state: appState, actions } = useContext(AppStoreContext);
  const { mapOverlayMachineService } = useMapOverlayMachine();

  const {
    punches,
    markups,
    queriedPunches,
    projects,
    selectedProject,
    selectedSheet,
  } = appState;

  const qPunches =
    isOnMap && sheets.size === 0
      ? Immutable.List()
      : queriedPunches
          .map((pid) => punches.get(pid, new Punch()))
          .filter((p) => p.id !== '');

  const pageIds: Array<string> = [];
  sheets.forEach((s) => {
    s.versions.forEach(({ pageid }) => pageIds.push(pageid));
  });

  const pageMarkups = pageIds
    .map((pageId) =>
      markups.get(pageId, Immutable.OrderedMap<MarkupId, Markup>() as MarkupMap)
    )
    .reduce(
      (acc, markupMap) => acc.merge(markupMap),
      Immutable.OrderedMap<MarkupId, Markup>()
    );

  const initPunchQuery = async (): Promise<void> => {
    const punchQuery = new PunchQuery({
      pages: Immutable.Set(pageIds),
    });
    await actions.setPunchQuery(punchQuery);
    await actions.queryPunches();
  };
  useEffect(() => {
    if (resetPunchQuery || appState.punchQuery.pages.size === 0) {
      initPunchQuery();
    }
  }, [selectedSheet]);

  const handlePunchListRowClick = (punch: Punch): void => {
    const isMapPunch = appState.interactions.some(
      (interaction) => interaction.type === 'ViewMapPunchList'
    );

    const punchMarkup = pageMarkups.get(punch.markupids.first());

    if (!isMapPunch && punchMarkup) {
      const { fileid } = punchMarkup;

      const interaction = new UpdatePunch({
        viewerOverlay: true,
        punch,
        fileid,
      });

      actions.clearInteractions();
      actions.createInteraction(interaction);
    } else if (isMapPunch && punchMarkup) {
      const { fileid } = punchMarkup;
      const interaction = new UpdatePunch({
        viewerOverlay: true,
        punch,
        fileid,
        containerStyles: {
          top: 96,
          height: `calc(100vh - 96px)`,
        },
      });

      mapOverlayMachineService.send({
        type: MapOverlayMachineEventsEnum.OPEN_UPDATE_PUNCH_OVERLAY,
        data: {
          interaction,
        },
      });
    }
  };

  const getSheetPunches = () => {
    // See if each punches' markups are one of the markups for a page.
    return qPunches
      .filter((p) => p.markupids.some((mid: string) => pageMarkups.has(mid)))
      .valueSeq()
      .toList();
  };

  const project = projects.get(selectedProject, new Project());

  return (
    <div className="bgd-white grow-1">
      <PunchListHeader punches={getSheetPunches()} onShowAll={initPunchQuery} />
      <div style={{ height: 'calc(100vh - 248px' }} className="oa">
        {qPunches.map((p) => (
          <PunchListRow
            key={p.id}
            project={project}
            punch={p}
            onPunchListRowClick={handlePunchListRowClick}
          />
        ))}
      </div>
    </div>
  );
};

export default withAppStore<PunchListProps>(PunchList);
